import React, { useState, useMemo } from 'react'
import { ArrowRight, ArrowLeft, Server, Info, Settings2, AlertTriangle } from 'lucide-react'

// VCN max capacity = 65,536 IPs (/16)
const VCN_TOTAL_IPS = 65536
const MAX_SUBNETS = 20 // Hard limit for OCI subnets per VCN

// VLSM option definitions with their CIDR prefixes and total IPs
const VLSM_OPTIONS = [
  { hosts: 5, prefix: 29, totalIps: 8, label: '/29' },
  { hosts: 13, prefix: 28, totalIps: 16, label: '/28' },
  { hosts: 29, prefix: 27, totalIps: 32, label: '/27' },
  { hosts: 61, prefix: 26, totalIps: 64, label: '/26' },
  { hosts: 125, prefix: 25, totalIps: 128, label: '/25' },
  { hosts: 253, prefix: 24, totalIps: 256, label: '/24' },
  { hosts: 509, prefix: 23, totalIps: 512, label: '/23' },
  { hosts: 1021, prefix: 22, totalIps: 1024, label: '/22' },
  { hosts: 2045, prefix: 21, totalIps: 2048, label: '/21' },
  { hosts: 4093, prefix: 20, totalIps: 4096, label: '/20' },
  { hosts: 8189, prefix: 19, totalIps: 8192, label: '/19' },
  { hosts: 16381, prefix: 18, totalIps: 16384, label: '/18' },
  { hosts: 32765, prefix: 17, totalIps: 32768, label: '/17 - largest valid OCI subnet' },
  { hosts: 65533, prefix: 16, totalIps: 65536, label: '/16 - uses entire VCN' },
]

/**
 * Get available VLSM options filtered by subnet count
 * Returns options where maxSubnets >= subnetCount
 * Also includes how many more subnets of each size can fit
 */
function getAvailableVlsmOptions(subnetCount) {
  return VLSM_OPTIONS.map(option => {
    const maxByIpCapacity = Math.floor(VCN_TOTAL_IPS / option.totalIps)
    const actualMax = Math.min(maxByIpCapacity, MAX_SUBNETS)
    const remainingSlots = Math.max(0, actualMax - subnetCount)
    return {
      ...option,
      maxSubnets: actualMax,
      remainingSlots,
      isAvailable: subnetCount <= actualMax
    }
  }).filter(option => option.isAvailable)
}

/**
 * Check if a specific host value is valid for the given subnet count
 */
function isHostValidForSubnetCount(hosts, subnetCount) {
  const option = VLSM_OPTIONS.find(opt => opt.hosts === hosts)
  if (!option) return false
  const maxSubnets = Math.floor(VCN_TOTAL_IPS / option.totalIps)
  const actualMax = Math.min(maxSubnets, MAX_SUBNETS)
  return subnetCount <= actualMax
}

/**
 * Get the next valid host option for a subnet count
 */
function getNextValidHostOption(subnetCount) {
  const availableOptions = getAvailableVlsmOptions(subnetCount)
  return availableOptions.length > 0 ? availableOptions[availableOptions.length - 1].hosts : 253
}

function Step2SubnetCount({ vcnCount, initialSubnetCounts, initialVcnNames, onComplete, onBack }) {
  const [subnetCounts, setSubnetCounts] = useState(initialSubnetCounts)
  const [vcnNames, setVcnNames] = useState(initialVcnNames)
  const [useVlsm, setUseVlsm] = useState(false)
  const [subnetHosts, setSubnetHosts] = useState(() =>
    Array(vcnCount).fill(null).map((_, i) =>
      Array(initialSubnetCounts[i] || 0).fill(253)
    )
  )
  const [error, setError] = useState('')
  const [warnings, setWarnings] = useState({}) // Track warnings per VCN for invalid selections

  const handleSubnetChange = (index, value) => {
    const num = parseInt(value, 10)
    if (!isNaN(num)) {
      const newCounts = [...subnetCounts]
      const oldCount = newCounts[index] || 0
      if (num < 0) {
        newCounts[index] = 0
      } else if (num > 20) {
        newCounts[index] = 20
      } else {
        newCounts[index] = num
      }
      setSubnetCounts(newCounts)

      // Update hosts array when subnet count changes
      if (useVlsm && newCounts[index] !== oldCount) {
        const newHosts = [...subnetHosts]
        const newWarnings = { ...warnings }

        if (newCounts[index] > oldCount) {
          // Add more subnets with default 253 hosts (/24)
          newHosts[index] = [
            ...(newHosts[index] || []),
            ...Array(newCounts[index] - oldCount).fill(253)
          ]
        } else {
          // Remove excess subnets
          newHosts[index] = (newHosts[index] || []).slice(0, newCounts[index])
        }

        // Check for invalid VLSM selections after subnet count change
        const currentHosts = newHosts[index] || []
        const invalidSelections = []
        currentHosts.forEach((hosts, si) => {
          if (!isHostValidForSubnetCount(hosts, newCounts[index])) {
            invalidSelections.push(si + 1)
          }
        })

        if (invalidSelections.length > 0) {
          newWarnings[index] = `Subnet${invalidSelections.length > 1 ? 's' : ''} ${invalidSelections.join(', ')} will exceed VCN capacity. Please select a smaller subnet size.`
        } else {
          delete newWarnings[index]
        }

        setSubnetHosts(newHosts)
        setWarnings(newWarnings)
      }

      setError('')
    }
  }

  const handleHostsChange = (vcnIndex, subnetIndex, value) => {
    const num = parseInt(value, 10)
    if (!isNaN(num) && num >= 0 && num <= 65534) {
      const newHosts = [...subnetHosts]
      if (!newHosts[vcnIndex]) newHosts[vcnIndex] = []
      newHosts[vcnIndex][subnetIndex] = num
      setSubnetHosts(newHosts)
    }
  }

  const handleNameChange = (index, value) => {
    const newNames = [...vcnNames]
    newNames[index] = value
    setVcnNames(newNames)
  }

  const handleContinue = () => {
    // Validate
    for (let i = 0; i < subnetCounts.length; i++) {
      if (subnetCounts[i] < 0 || subnetCounts[i] > 20) {
        setError(`VCN ${i + 1}: Subnet count must be between 0 and 20`)
        return
      }
    }

    // Use default names if empty
    const finalNames = vcnNames.map((name, i) =>
      name.trim() || `VCN ${i + 1}`
    )

    onComplete(subnetCounts, finalNames, useVlsm ? subnetHosts : null, useVlsm)
  }

  const totalSubnets = subnetCounts.reduce((sum, count) => sum + count, 0)

  return (
    <div className="step-card">
      <div className="step-header">
        <h2>Configure Subnets per VCN</h2>
        <p className="step-description">
          Specify how many subnets each VCN should have (0-20 per VCN)
        </p>
      </div>

      <div className="step-content">
        {/* VLSM Toggle */}
        <div className="vlsm-toggle-section">
          <div className="vlsm-toggle-header">
            <Settings2 size={18} />
            <span>Subnet Sizing Mode</span>
          </div>
          <div className="vlsm-toggle-buttons">
            <button
              className={`vlsm-toggle-btn ${!useVlsm ? 'active' : ''}`}
              onClick={() => setUseVlsm(false)}
            >
              Equal Size
              <span className="vlsm-desc">All subnets same size</span>
            </button>
            <button
              className={`vlsm-toggle-btn ${useVlsm ? 'active' : ''}`}
              onClick={() => setUseVlsm(true)}
            >
              VLSM (Variable)
              <span className="vlsm-desc">Size based on host count</span>
            </button>
          </div>
        </div>

        <div className="vcn-subnet-list">
          {Array.from({ length: vcnCount }, (_, i) => (
            <div key={i} className="vcn-subnet-item">
              <div className="vcn-subnet-header">
                <div className="vcn-badge">VCN {i + 1}</div>
                <input
                  type="text"
                  className="vcn-name-input"
                  placeholder={`VCN ${i + 1} name (optional)`}
                  value={vcnNames[i] || ''}
                  onChange={(e) => handleNameChange(i, e.target.value)}
                />
              </div>

              <div className="subnet-count-control">
                <label className="subnet-label">
                  <Server size={16} />
                  Number of Subnets
                </label>
                <div className="number-input-wrapper small">
                  <button
                    className="number-btn"
                    onClick={() => handleSubnetChange(i, Math.max(0, subnetCounts[i] - 1))}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    className="number-input"
                    value={subnetCounts[i]}
                    onChange={(e) => handleSubnetChange(i, e.target.value)}
                    min="0"
                    max="20"
                  />
                  <button
                    className="number-btn"
                    onClick={() => handleSubnetChange(i, Math.min(20, subnetCounts[i] + 1))}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* VLSM Host Count Inputs */}
              {useVlsm && subnetCounts[i] > 0 && (
                <div className="vlsm-hosts-section">
                  <label className="vlsm-label"># of usable IP addresses per subnet (for VLSM calculation)</label>
                  {/* Capacity info bar */}
                  <div className="vlsm-capacity-info">
                    <Info size={14} />
                    <span>
                      VCN capacity: {VCN_TOTAL_IPS.toLocaleString()} IPs (/16).
                      With {subnetCounts[i]} subnet{subnetCounts[i] !== 1 ? 's' : ''}, each can be up to /{Math.max(16, 32 - Math.floor(Math.log2(VCN_TOTAL_IPS / subnetCounts[i])))}
                    </span>
                  </div>
                  <div className="vlsm-hosts-grid">
                    {Array.from({ length: subnetCounts[i] }, (_, si) => {
                      const currentValue = subnetHosts[i]?.[si] || 253
                      const isInvalid = !isHostValidForSubnetCount(currentValue, subnetCounts[i])
                      const availableOptions = getAvailableVlsmOptions(subnetCounts[i])

                      return (
                        <div key={si} className={`vlsm-host-input ${isInvalid ? 'invalid' : ''}`}>
                          <span className="vlsm-host-name">S{si + 1}</span>
                          <select
                            className={`vlsm-select ${isInvalid ? 'invalid' : ''}`}
                            value={currentValue}
                            onChange={(e) => handleHostsChange(i, si, e.target.value)}
                          >
                            {availableOptions.map(option => (
                              <option key={option.hosts} value={option.hosts}>
                                {option.hosts.toLocaleString()} usable IPs ({option.label})
                                {option.remainingSlots > 0 && option.remainingSlots < 20 && ` - can fit ${option.remainingSlots} more`}
                              </option>
                            ))}
                            {/* Show current value as disabled option if it's invalid */}
                            {isInvalid && (
                              <option value={currentValue} disabled>
                                {currentValue.toLocaleString()} usable IPs (invalid - too large for {subnetCounts[i]} subnets)
                              </option>
                            )}
                          </select>
                          {isInvalid && (
                            <div className="vlsm-invalid-indicator">
                              <AlertTriangle size={14} />
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  {/* Warning message for this VCN */}
                  {warnings[i] && (
                    <div className="vlsm-warning">
                      <AlertTriangle size={16} />
                      <span>{warnings[i]}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="summary-bar">
          <span>Total: {vcnCount} VCN{vcnCount !== 1 ? 's' : ''} with {totalSubnets} subnet{totalSubnets !== 1 ? 's' : ''}</span>
        </div>

        {error && (
          <div className="error-message">
            <Info size={16} />
            {error}
          </div>
        )}

        <div className="info-box">
          <h3>{useVlsm ? 'VLSM (Variable Length Subnet Masking)' : 'Subnet Information'}</h3>
          <p>
            {useVlsm
              ? 'VLSM allows you to specify the exact number of hosts needed per subnet. The calculator will determine the appropriate subnet size for each subnet individually, minimizing IP address waste.'
              : 'Subnets are subdivisions of a VCN\'s IP address range. Each subnet can span multiple availability domains and contains resources like compute instances, load balancers, and databases. You can have up to 20 subnets per VCN.'}
          </p>
        </div>
      </div>

      <div className="step-footer">
        <button className="btn btn-secondary" onClick={onBack}>
          <ArrowLeft size={18} />
          Back
        </button>
        <button className="btn btn-primary btn-large" onClick={handleContinue}>
          Continue to Base CIDR Selection
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  )
}

export default Step2SubnetCount
